import {
  initTRPC,
  type TRPCMutationProcedure,
  type TRPCQueryProcedure,
  type TRPCSubscriptionProcedure,
} from '@trpc/server';
import type { Context } from '../types/Context.js';
import { z } from 'zod/v4-mini';
import type { ApiParams } from '../types/ApiParams.js';
import type { Session } from '@ms-cloudpack/common-types';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- not exported from v4-mini
import type { ZodError } from 'zod/v4';

export type CreateContext = () => Promise<Context>;

/**
 * tRPC backend (the initialization functions should only be called once).
 * Exported for testing.
 */
export const trpc = initTRPC.context<CreateContext>().create({
  errorFormatter(opts) {
    const { shape, error } = opts;
    return {
      ...shape,
      message:
        // For zod validation errors, make them more readable (can't use instanceof here because
        // ZodError is missing from the v4-mini exports; the actual class name is $ZodError).
        // This is tested in the `getData` and `onDataChanged` tests.
        error.code === 'BAD_REQUEST' && error.cause?.name.endsWith('ZodError')
          ? z.prettifyError(error.cause as ZodError)
          : error.message,
    };
    // };
  },
});

/** Reusable TRPC router helper. */
export const createRouter = trpc.router;

/**
 * Generic procedure supertype for the procedure helpers. This should only be used in defining
 * procedure helpers (not as a type on functions) because it expects all Context/Session keys
 * rather than narrowing to only the ones used.
 */
type Procedure<TInput, TOutput> = (opts: ApiParams<TInput, keyof Context, keyof Session>) => TOutput | Promise<TOutput>;

/**
 * tRPC procedure definition type. `metadata` is marked as `never` because we're not currently
 * using the `.metadata()` feature of procedures (could be added later).
 */
type ProcedureDef<TInput, TOutput> = { input: TInput; output: TOutput; meta: never };

/**
 * Mutation procedure helper for tRPC.
 *
 * It's recommended to explicitly specify the type parameters to ensure the original types
 * and any docs are preserved in the procedures exposed on the client.
 */
// These helpers exist because for some reason the generic input/output type inference in
// `t.procedure.mutation` etc (maybe when combined with the way zod defines input/output types)
// loses the original types during declaration emit, and instead emits copies with all the same
// properties but no docs.
export function mutationProcedure<TInput, TOutput>(
  procedure: Procedure<TInput, TOutput>,
  input: z.ZodMiniType<TInput, TInput>,
  output: z.ZodMiniType<TOutput, TOutput>,
): TRPCMutationProcedure<ProcedureDef<TInput, TOutput>> {
  return (
    trpc.procedure
      .input(input)
      .output(output)
      // eslint-disable-next-line
      .mutation(procedure as any) as TRPCMutationProcedure<ProcedureDef<TInput, TOutput>>
  );
}

/**
 * Query procedure helper for tRPC.
 *
 * It's recommended to explicitly specify the type parameters to ensure the original types
 * and any docs are preserved in the procedures exposed on the client.
 */
export function queryProcedure<TInput, TOutput>(
  procedure: Procedure<TInput, TOutput>,
  input: z.ZodMiniType<TInput, TInput>,
  output: z.ZodMiniType<TOutput, TOutput>,
): TRPCQueryProcedure<ProcedureDef<TInput, TOutput>> {
  return (
    trpc.procedure
      .input(input)
      .output(output)
      // eslint-disable-next-line
      .query(procedure as any) as TRPCQueryProcedure<ProcedureDef<TInput, TOutput>>
  );
}

/**
 * Subscription procedure helper for tRPC.
 *
 * It's recommended to explicitly specify the type parameters to ensure the original types
 * and any docs are preserved in the procedures exposed on the client.
 */
export function subscriptionProcedure<TInput, TOutput>(
  procedure: Procedure<TInput, TOutput>,
  input: z.ZodMiniType<TInput, TInput>,
  output: z.ZodMiniType<TOutput, TOutput>,
): TRPCSubscriptionProcedure<ProcedureDef<TInput, TOutput>> {
  return (
    trpc.procedure
      .input(input)
      .output(output)
      // eslint-disable-next-line
      .subscription(procedure as any) as TRPCSubscriptionProcedure<ProcedureDef<TInput, TOutput>>
  );
}
